/**
 * Klaviyo Popup Component
 * Handles popup display logic, form submission, and user interactions
 */

class KlaviyoPopup {
  constructor() {
    this.popup = document.querySelector('.klaviyo-popup');
    if (!this.popup) return;

    this.overlay = this.popup.querySelector('.klaviyo-popup__overlay');
    this.modal = this.popup.querySelector('.klaviyo-popup__modal');
    this.closeBtn = this.popup.querySelector('.klaviyo-popup__close');
    this.form = this.popup.querySelector('.klaviyo-popup__form');
    this.emailInput = this.popup.querySelector('.klaviyo-popup__email-input');
    this.submitBtn = this.popup.querySelector('.klaviyo-popup__submit-btn');

    this.consentCheckbox = this.form.querySelector('input[name="contact[accepts_marketing]"]');
    // this is injected by shopify klaviyo-popup.liquid in prod. for local use below:
    // this.popup.dataset.edgeFunctionUrl = 'http://localhost:3000';

    this.delay = parseInt(this.popup.dataset.popupDelay) * 1000 || 3000;
    this.klaviyoListId = this.popup.dataset.klaviyoListId;
    this.isVisible = false;
    this.hasBeenShown = false;

    this.init();
  }

  init() {
    if (Shopify.designMode) return;
    this.trackPageVisit();
    this.bindEvents();
    this.schedulePopup();
    console.log('Klaviyo popup initialized');
  }

  trackPageVisit() {
    // Get current page visit count from session storage
    let pageVisitCount = parseInt(sessionStorage.getItem('klaviyo-page-visit-count')) || 0;

    // Increment the counter
    pageVisitCount++;

    // Store the updated count
    sessionStorage.setItem('klaviyo-page-visit-count', pageVisitCount.toString());

    console.log(`Klaviyo popup: Page visit count = ${pageVisitCount}`);
  }

  bindEvents() {
    // Close popup events
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.hidePopup();
      });
    }

    // Close on overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.hidePopup(false);
        }
      });
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.hidePopup();
      }
    });

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleFormSubmission();
        return false;
      });
    }

    // Email input validation
    if (this.emailInput) {
      this.emailInput.addEventListener('input', () => {
        this.validateForm();
      });
    }

    if (this.consentCheckbox) {
      this.consentCheckbox.addEventListener('change', () => {
        this.validateForm();
      });
    }
  }

  schedulePopup() {
    // Check if popup should be shown
    console.log('Scheduling Klaviyo popup check');
    if (this.shouldShowPopup()) {
      console.log('Klaviyo popup will be shown after delay');
      // Check if Klaviyo session exists
      let hasSession = false;

      klaviyo.isIdentified((isIdentified) => {
        console.log('isIdentified', isIdentified);
        if (isIdentified) {
          hasSession = true;
        }
      });
      

      // Wait a bit to see if callbacks fire
      setTimeout(() => {
        if (!hasSession) {
          // Run your function when there's no session/account
          this.runPopupLogic();
          console.log('No Klaviyo session found');
        }
      }, 1000);
    } else {
      console.log('Klaviyo popup will NOT be shown');
    }
  }

  runPopupLogic() {
    if (window.cookieBannerVisible || window.countryPopupVisible) {
      // Wait for banner to be dismissed
      console.log('Waiting for cookie banner to hide before showing popup');
      const onBannerHidden = () => {
        console.log('Cookie banner hidden, showing popup');
        setTimeout(() => { this.showPopup(); }, this.delay);
        document.removeEventListener('cookieBannerHidden', onBannerHidden);
      };
      document.addEventListener('cookieBannerHidden', onBannerHidden);
    } else {
      console.log('Showing popup immediately');
      setTimeout(() => { this.showPopup(); }, this.delay);
    }
  }

  shouldShowPopup() {
    // Check if user has visited at least 4 pages in this session
    const pageVisitCount = parseInt(sessionStorage.getItem('klaviyo-page-visit-count')) || 0;
    if (pageVisitCount < 4) {
      console.log(`Klaviyo popup: Only ${pageVisitCount} page(s) visited, need 4`);
      return false;
    }

    // Don't show if user has dismissed it recently (24 hours)
    const dismissedTime = localStorage.getItem('klaviyo-popup-dismissed');
    if (dismissedTime) {
      const now = new Date().getTime();
      const dismissedTimestamp = parseInt(dismissedTime);
      const hoursSinceDismissed = (now - dismissedTimestamp) / (1000 * 60 * 60);

      if (hoursSinceDismissed < 24) {
        console.log('Klaviyo popup dismissed recently');
        return false;
      }
    }

    // Don't show if user is already subscribed
    if (localStorage.getItem('klaviyo-popup-subscribed')) {
      console.log('User already subscribed to Klaviyo popup list');
      return false;
    }

    return true;
  }

  showPopup() {
    console.log('Attempting to show Klaviyo popup', this.hasBeenShown, this.popup);
    if (this.hasBeenShown || !this.popup) return;

    console.log('Showing Klaviyo popup');
    this.isVisible = true;
    this.hasBeenShown = true;
    this.popup.classList.add('is-visible');
    this.popup.classList.remove('is-hidden');

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Focus email input after animation
    setTimeout(() => {
      if (this.emailInput) {
        this.emailInput.focus();
      }
    }, 300);

    // Track popup view (optional - for analytics)
    this.trackEvent('popup_viewed');
    document.body.classList.add('overlay-open');
  }

  hidePopup(saveDismissal = true) {
    if (!this.isVisible || !this.popup) return;

    this.popup.classList.add('is-closing');

    setTimeout(() => {
      this.isVisible = false;
      this.popup.classList.remove('is-visible', 'is-closing');
      this.popup.classList.add('is-hidden');

      // Restore body scroll
      document.body.style.overflow = '';
    }, 200);

    // Store dismissal time
    if (saveDismissal) {
      localStorage.setItem('klaviyo-popup-dismissed', new Date().getTime().toString());
    }

    // Track popup dismissal
    this.trackEvent('popup_dismissed');
    document.body.classList.remove('overlay-open');
  }

  validateForm() {
    if (!this.emailInput || !this.submitBtn) return;

    const email = this.emailInput.value.trim();
    let isValid = this.isValidEmail(email);

    // If there's a consent checkbox, ensure it's checked
    if (this.consentCheckbox && isValid) {
      isValid = this.consentCheckbox.checked;
    }

    this.submitBtn.disabled = !isValid;

    return isValid;
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  async handleFormSubmission() {
    if (!this.validateForm()) return;

    const email = this.emailInput.value.trim();

    const hasConsent = !this.consentCheckbox || this.consentCheckbox.checked;

    if (this.consentCheckbox && !hasConsent) {
      this.showError('Please agree to the privacy policy to continue.');
      return;
    }

    // Disable form during submission
    this.setFormLoading(true);

    try {
      // Use edge function for Klaviyo subscription
      const edgeFunctionUrl = this.popup.dataset.edgeFunctionUrl;
      if (!edgeFunctionUrl) {
        throw new Error('Edge function URL not configured');
      }

      await this.subscribeViaEdgeFunction(email, edgeFunctionUrl);
      this.handleSuccessfulSubmission(email);
    } catch (error) {
      console.error('Form submission error:', error);

      // Check if it's an "already subscribed" error
      if (error.message && error.message.includes('already subscribed')) {
        this.showError('This email is already subscribed.');
      } else {
        this.showError('Something went wrong. Please try again.');
      }

      this.setFormLoading(false);
    }
  }

  async subscribeViaEdgeFunction(email, edgeFunctionUrl) {
    const response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Handle specific status codes
      if (response.status === 409) {
        throw new Error('Email is already subscribed');
      }

      if (response.status === 400) {
        throw new Error(errorData.message || errorData.error || 'Invalid email format');
      }

      if (response.status === 500) {
        throw new Error(errorData.message || errorData.error || 'Server error occurred');
      }

      // Generic error fallback with more detail
      throw new Error(errorData.message || errorData.error || `Server error: ${response.status}`);
    }

    const result = await response.json();

    // Check if the response contains an error field (from checkIfEmailSubscribed)
    if (result.error) {
      throw new Error(result.error);
    }

    return result;
  }

  handleSuccessfulSubmission(email) {
    // Track successful submission
    this.trackEvent('popup_subscribed', { email });

    // Mark as subscribed
    localStorage.setItem('klaviyo-popup-subscribed', 'true');

    // Show success message
    this.showSuccessMessage();

    // Hide popup after delay
    setTimeout(() => {
      this.hidePopup();
    }, 2000);
  }

  showSuccessMessage() {
    let successMessage = this.form.querySelector('.klaviyo-popup__success-message');

    if (!successMessage) {
      successMessage = document.createElement('div');
      successMessage.className = 'klaviyo-popup__success-message';
      successMessage.textContent = 'Thank you for subscribing!';
      this.form.appendChild(successMessage);
    }

    successMessage.classList.add('is-visible');
    this.form.classList.add('is-submitted');
  }

  showError(message) {
    // Remove existing error
    const existingError = this.form.querySelector('.klaviyo-popup__error-message');
    if (existingError) {
      existingError.remove();
    }

    // Create error message
    const errorDiv = document.createElement('div');
    errorDiv.className = 'klaviyo-popup__error-message';
    errorDiv.style.cssText = `
      color: #dc3545;
      font-size: 12px;
      margin-top: 10px;
      text-align: center;
    `;
    errorDiv.textContent = message;

    this.form.appendChild(errorDiv);

    // Remove error after 5 seconds
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.remove();
      }
    }, 5000);
  }

  setFormLoading(isLoading) {
    if (!this.submitBtn) return;

    if (isLoading) {
      this.submitBtn.disabled = true;
      this.submitBtn.textContent = 'SUBMITTING...';
    } else {
      // When not loading, respect form validation state
      this.submitBtn.textContent = this.submitBtn.dataset.originalText || 'JOIN THE SKINCARE SOCIETY';
      this.validateForm(); // Re-validate to set correct disabled state
    }

    if (!this.submitBtn.dataset.originalText && !isLoading) {
      this.submitBtn.dataset.originalText = this.submitBtn.textContent;
    }
  }

  trackEvent(eventName, data = {}) {
    // Track events for analytics (Google Analytics, Klaviyo, etc.)
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, {
        event_category: 'popup',
        ...data
      });
    }

    // Klaviyo tracking (if Klaviyo is loaded)
    if (typeof _learnq !== 'undefined') {
      _learnq.push(['track', eventName, data]);
    }

    // Custom event for other tracking systems
    window.dispatchEvent(new CustomEvent('klaviyo-popup-event', {
      detail: { eventName, data }
    }));
  }

  // Public methods for external control
  show() {
    this.showPopup();
  }

  hide() {
    this.hidePopup();
  }

  reset() {
    // Reset all stored states
    sessionStorage.removeItem('klaviyo-popup-shown');
    sessionStorage.removeItem('klaviyo-page-visit-count');
    localStorage.removeItem('klaviyo-popup-dismissed');
    localStorage.removeItem('klaviyo-popup-subscribed');
  }
}

// Initialize popup when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.klaviyoPopup = new KlaviyoPopup();
});

// Make popup globally accessible for debugging/external control
window.KlaviyoPopup = KlaviyoPopup;